import { subscribeToLocation } from "@swan-io/chicane";
import { ResultOf } from "gql.tada";
import { Fragment, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { OrganizationListQuery } from "../pages/OrgListPage";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { useQuery } from "../utils/graphql";
import { t } from "../utils/i18n";
import { router } from "../utils/routes";
import { IconAvatar, OrgAvatar, PlaceholderAvatar } from "./Avatar";
import { Icon } from "./Icon";
import { Popover, PopoverSeparator } from "./Popover";
import { Fill, Space } from "./Space";
import { PressableLink, Text } from "./Text";

const styles = StyleSheet.create({
  popoverButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.neutral[800],
    borderWidth: 1,
    borderColor: colors.neutral[700],
    borderRadius: radii[4],
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
    outlineWidth: 0,
  },
  popoverButtonHover: {
    backgroundColor: colors.neutral[700],
  },

  popoverRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
    outlineWidth: 0,
  },
  popoverRowHover: {
    backgroundColor: colors.neutral[800],
  },

  plusIcon: {
    backgroundColor: colors.neutral[800],
  },
});

type Props = {
  orgSlug?: string;
};

export const OrganizationSelect: React.FC<Props> = ({ orgSlug }) => {
  const ref = useRef<View | null>(null);
  const { data } = useQuery(OrganizationListQuery);

  const currentOrg = data.organizations.find((org) => org.slug === orgSlug);

  const [opened, setOpened] = useState(false);

  const togglePopover = () => {
    setOpened((prev) => !prev);
  };

  const closePopover = () => {
    setOpened(false);
  };

  useEffect(() => {
    const unsubscribe = subscribeToLocation(closePopover);
    return unsubscribe;
  }, []);

  return (
    <>
      <Pressable
        ref={ref}
        style={({ pressed, focused, hovered }) => [
          styles.popoverButton,
          (pressed || focused || hovered) && styles.popoverButtonHover,
        ]}
        onPress={togglePopover}
      >
        {currentOrg != null ? (
          <OrgAvatar
            size="regular"
            name={currentOrg.name}
            url={currentOrg.logoUrl}
            updatedAt={currentOrg.updatedAt}
          />
        ) : (
          <PlaceholderAvatar />
        )}

        <Space width={12} />

        <Text variant="body" color={invariantColor.white}>
          {currentOrg?.name ?? t("component.org_select.placeholder")}
        </Text>

        <Fill minWidth={12} />

        <Icon name="unfold" size={20} color={colors.neutral[600]} />
      </Pressable>

      <Popover
        target={ref}
        opened={opened}
        placement="bottom"
        offset={12}
        fitTargetWidth={true}
        onClose={closePopover}
      >
        <OrganizationList currentOrg={currentOrg} organizations={data.organizations} />
      </Popover>
    </>
  );
};

type OrganizationListProps = {
  currentOrg: ResultOf<typeof OrganizationListQuery>["organizations"][number] | undefined;
  organizations: ResultOf<typeof OrganizationListQuery>["organizations"];
};

const OrganizationList: React.FC<OrganizationListProps> = ({ currentOrg, organizations }) => {
  return (
    <>
      {organizations
        .filter((org) => org.id !== currentOrg?.id)
        .map((org) => (
          <Fragment key={org.id}>
            <PressableLink
              key={org.id}
              to={router.OrgHome({ org_slug: org.slug })}
              style={({ pressed, focused, hovered }) => [
                styles.popoverRow,
                (pressed || focused || hovered) && styles.popoverRowHover,
              ]}
            >
              <OrgAvatar
                size="regular"
                name={org.name}
                url={org.logoUrl}
                updatedAt={org.updatedAt}
              />

              <Space width={12} />

              <Text variant="body" color={invariantColor.white}>
                {org.name}
              </Text>
            </PressableLink>

            <PopoverSeparator />
          </Fragment>
        ))}

      <PressableLink
        to={router.OrgNewIndex()}
        style={({ pressed, focused, hovered }) => [
          styles.popoverRow,
          (pressed || focused || hovered) && styles.popoverRowHover,
        ]}
      >
        <IconAvatar
          size="regular"
          name="plus"
          color={colors.primary[500]}
          style={styles.plusIcon}
        />

        <Space width={12} />

        <Text variant="body" color={invariantColor.white}>
          {t("sidebar.create_organization")}
        </Text>
      </PressableLink>
    </>
  );
};
